import React from "react";

import CountResult from "common/entities/CountResult";
import { FragmentProvider } from "core/components/FragmentProvider";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { IQueryRoot, QueryRoot } from "core/decorators/QueryRoot";
import initialState from "core/reducers/initialState";
import { FragmentLayout } from "../../core/components/FragmentLayout";
import labelProvider from './Catalog.labels';
import CatalogLabels from "./Catalog.labels";
import * as catalogActions from "./catalogActions";
import CourseListPanelConfig from "./config/CourseListPanelConfig";
import Badge from "./entities/Badge";
import Course from "./entities/Course";
import { courseParamNames, ICourseParams } from "./entities/CourseParams";
import * as badgeFragments from "./fragments/badge/displayFragments";
import * as itemContainerFragments from "./fragments/container/itemContainerFragments";
import * as parentContainerFragments from "./fragments/container/parentContainerFragments";
import * as actionFragments from "./fragments/course/actionFragments";
import * as compositeFragments from "./fragments/course/compositeFragments";
import * as displayFragments from "./fragments/course/displayFragments";
import ICourseFragmentProps from "./fragments/course/ICourseFragmentProps";
import * as filterFragments from "./fragments/filterFragments";
import * as miscFragments from "./fragments/generalFragments";
import { ICatalogMetaProps } from "./fragments/ICatalogMetaProps";
import { ContributorCatalog } from "./entities/ProductContributor";
import Query from "core/api/QueryComponent";
import Loading from "common/fragments/Loading";
import { Spinner } from "@blueprintjs/core";
// tslint:disable:max-classes-per-file variable-name

// panel level fragments for intro and filtering
const generalFragments = {
    ...filterFragments,
    ...miscFragments
};

// what type of list
const ContainerFragments = FragmentProvider.forFragments("catalog-container", parentContainerFragments);

// what type of course wrapper
const ItemFragments = FragmentProvider.forFragments("catalog-item", itemContainerFragments);

// the info available for a course
const courseFragments = {
    ...actionFragments,
    ...displayFragments,
    ...compositeFragments,
};

const now = (new Date()).toISOString();


@Panel({ id: 'contributor-course-list', group: "catalog", configClass: CourseListPanelConfig, labelProvider, pageParams: true })
class CourseListPanel extends React.Component<IPanelProps<CourseListPanelConfig> & IQueryRoot<ICourseParams>, { container: string }> {
    public state = { container: null };

    private setParams = (newParams: ICourseParams) => {
        const { actions } = this.props;

        actions.pushPageParams(...Object.entries(newParams).map(([key, value]) => ({ key, value })));
    }

    private replaceParams = (newParams: ICourseParams) => {
        const { actions } = this.props;

        actions.replacePageParams(...Object.entries(newParams).map(([key, value]) => ({ key, value })));
    }

    componentWillUnmount() {
        this.props.actions.removePageParams(["contributorId"]);
    }

    private renderItem = (item: Course, index: number) => {
        const { config, queryParams, actions } = this.props;

        const courseProps: ICourseFragmentProps = {
            course: item,
            params: queryParams,
            actions: catalogActions,
            pageActions: actions,
            renderBadge: this.renderBadge,
            showNew: config.newBanner,
            disableDirectBuy: config.disableDirectBuy,
            loginBeforeBuy: config.loginBeforeBuy,
            detailsLinkVisible: true,
            showCustomFields: config.showCustomFields
        };

        const itemKey = item && item.id || "course-" + index;

        return (
            <ItemFragments fragments={config.itemFragment} fragmentProps={{ key: itemKey }}>
                <FragmentLayout layout={config.courseFragmentLayout} fragments={courseFragments} props={courseProps} />
            </ItemFragments>
        );
    }

    public render() {
        const { config, queryParams, queryState } = this.props;
        const queryString = window.location.search;
        const params1 = new URLSearchParams(queryString);
        const paramValue = params1.get('contributorId');
        const params = { contributorId: paramValue };
        return (
            <Query.Many entity={ContributorCatalog} args={params} >
                {
                    ({ data, loading }) => {

                        if (loading) {
                            return (
                                <Spinner size={Spinner.SIZE_STANDARD} />
                            );
                        }

                        if (!data || !(data && data.length)) {
                            return this.renderNotFound();
                        }
                        let count = {
                            count: 0
                        };
                        let pageSize = 20;
                        let sort = "";
                        let sortByModifyDays = 0;
                        if (queryParams) {
                            pageSize = queryParams.pageSize ? queryParams.pageSize : 20;
                            sort = queryParams.sort ? queryParams.sort : "-startDate";
                            sortByModifyDays = queryParams.sortByModifyDays ? queryParams.sortByModifyDays : 0;
                        }
                        const itemIds = data.filter(contributor => contributor.product !== null) // Filter out null products
                            .map(contributor => contributor.product['id']);         // Map to extract product IDs
                        // const itemIds = data.map(contributor => contributor.id);
                        const params = {
                            itemIds, sortByModifyDays, pageSize, sort
                        }
                        if (itemIds && itemIds.length > 0) {
                            return <Query.Many entity={Course} args={params}>
                                {({ data, loading }) => {
                                    if (loading) {
                                        return <Loading />;
                                    }

                                    const courses = data ? data : [];
                                    let resultsElement;
                                    // TODO make this a layout
                                    if (!loading && (courses == null || courses.length === 0)) {
                                        resultsElement = <CatalogLabels.Labels.NoResults />;
                                    } else {
                                        const courseItemElements = Array.isArray(courses) && courses.map(this.renderItem);

                                        resultsElement = (
                                            <ContainerFragments
                                                fragments={this.state.container || config.containerFragment}
                                                fragmentProps={{
                                                    courses,
                                                    renderItem: this.renderItem,
                                                    params: queryParams,
                                                    courseFragments: Object.values(courseFragments) as any,
                                                    children: courseItemElements,
                                                    actions: catalogActions
                                                }}
                                            />
                                        );
                                    }

                                    const generalProps: ICatalogMetaProps = {
                                        loading: loading,
                                        params: this.props.pageParams,
                                        setParams: this.setParams,
                                        replaceParams: this.replaceParams,
                                        queryMeta: {
                                            count: courses ? courses.length : 0,
                                            pageNumber: 0,
                                            pageSize: pageSize,
                                            total: count && count.count || 0,
                                            initialPageSize: 20,
                                        },
                                        viewOptions: {
                                            container: this.state.container || config.containerFragment,
                                            setContainer: (container) => this.setState({ container })
                                        },
                                        resultsElement
                                    };

                                    return (
                                        <FragmentLayout layout={config.mainLayout} fragments={{ ...generalFragments }} props={{ ...generalProps }} />
                                    );
                                }}
                            </Query.Many>
                        }
                        else {
                            return <CatalogLabels.Labels.NoResults />
                        }
                    }}
            </Query.Many>
        );
    }

    private renderBadge = (badge: Badge) => {
        const { config } = this.props;
        return (
            <FragmentLayout fragments={badgeFragments} layout={config.badgeLayout} props={{ badge }} />
        );
    }
    private renderNotFound = () => {
        return (
            <CatalogLabels.Labels.NoResults />
        );
    }

}



// WEBPACK FOOTER //
// ./src/panels/catalog/ContributorCatalog.panel.tsx